import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ServiceCard from './components/ServiceCard';
import ProcessStep from './components/ProcessStep';
import TechnologyStack from './components/TechnologyStack';
import PricingCard from './components/PricingCard';
import SupportPackage from './components/SupportPackage';
import CommunicationProtocol from './components/CommunicationProtocol';

const Services = () => {
  const services = [
    {
      icon: "Code2",
      title: "Full-Stack Development",
      description: "End-to-end web application development with modern frameworks and scalable architecture. From concept to deployment, I build production-ready solutions that grow with your business.",
      features: [
        "React/Next.js frontend development",
        "Node.js/Express backend architecture",
        "RESTful API design and implementation",
        "Database design and optimization",
        "Third-party integrations",
        "Performance optimization"
      ],
      isHighlighted: true
    },
    {
      icon: "Layout",
      title: "System Architecture",
      description: "Strategic technical planning and infrastructure design for scalable, maintainable applications. I architect solutions that balance immediate needs with long-term growth.",
      features: [
        "Technical stack recommendations",
        "Scalability planning",
        "Security architecture",
        "Cloud infrastructure setup",
        "Microservices design",
        "Documentation standards"
      ]
    },
    {
      icon: "Zap",
      title: "Performance Optimization",
      description: "Speed and efficiency improvements for existing applications. I identify bottlenecks and implement solutions that deliver measurable performance gains.",
      features: [
        "Load time reduction",
        "Database query optimization",
        "Code refactoring",
        "Caching strategies",
        "CDN implementation",
        "Monitoring setup"
      ]
    },
    {
      icon: "Users",
      title: "Technical Consulting",
      description: "Expert guidance for technical decisions, team leadership, and project planning. I help startups and agencies make informed technology choices.",
      features: [
        "Technology stack evaluation",
        "Code review and audits",
        "Team mentorship",
        "Project estimation",
        "Risk assessment",
        "Best practices implementation"
      ]
    }
  ];

  const processSteps = [
    {
      number: 1,
      icon: "MessageSquare",
      title: "Discovery & Planning",
      description: "We start with a comprehensive discussion of your business goals, technical requirements, and success metrics. I analyze your needs and propose a tailored solution with clear deliverables and timeline.",
      duration: "3-5 days",
      deliverables: [
        "Project scope document",
        "Technical architecture proposal",
        "Timeline and milestones",
        "Cost breakdown"
      ]
    },
    {
      number: 2,
      icon: "Palette",
      title: "Design & Prototyping",
      description: "I create wireframes and interactive prototypes to validate the user experience before development begins. This ensures alignment on functionality and design direction.",
      duration: "5-7 days",
      deliverables: [
        "Wireframes and mockups",
        "Interactive prototype",
        "User flow diagrams",
        "Design system documentation"
      ]
    },
    {
      number: 3,
      icon: "Code",
      title: "Development & Testing",
      description: "Iterative development with regular check-ins and demos. I build features in priority order, ensuring core functionality is production-ready early in the process.",
      duration: "2-8 weeks",
      deliverables: [
        "Working application",
        "Automated test suite",
        "Code documentation",
        "Weekly progress reports"
      ]
    },
    {
      number: 4,
      icon: "Rocket",
      title: "Deployment & Launch",
      description: "Smooth deployment to production with monitoring, performance optimization, and launch support. I ensure your application is stable, secure, and ready for users.",
      duration: "3-5 days",
      deliverables: [
        "Production deployment",
        "Performance monitoring",
        "Security audit",
        "Launch checklist completion"
      ]
    },
    {
      number: 5,
      icon: "LifeBuoy",
      title: "Support & Maintenance",
      description: "Ongoing support to keep your application running smoothly. I provide bug fixes, feature updates, and technical guidance as your product evolves.",
      duration: "Ongoing",
      deliverables: [
        "Bug fixes and updates",
        "Performance monitoring",
        "Security patches",
        "Feature enhancements"
      ]
    }
  ];

  const technologyStacks = [
    {
      category: "Frontend Development",
      icon: "Monitor",
      description: "Modern, responsive user interfaces built with industry-leading frameworks",
      technologies: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Framer Motion", "Redux"]
    },
    {
      category: "Backend Development",
      icon: "Server",
      description: "Scalable server-side architecture with robust API design",
      technologies: ["Node.js", "Express", "PostgreSQL", "MongoDB", "Redis", "GraphQL"]
    },
    {
      category: "Cloud & DevOps",
      icon: "Cloud",
      description: "Reliable deployment and infrastructure management",
      technologies: ["AWS", "Vercel", "Docker", "GitHub Actions", "Nginx", "CloudFlare"]
    },
    {
      category: "Tools & Testing",
      icon: "Wrench",
      description: "Quality assurance and development workflow optimization",
      technologies: ["Jest", "Cypress", "Git", "Postman", "Figma", "VS Code"]
    }
  ];

  const pricingPackages = [
    {
      title: "MVP Launch",
      description: "Perfect for startups validating product-market fit",
      priceRange: "$8K - $15K",
      features: [
        "Core feature development",
        "Responsive web application",
        "User authentication system",
        "Database setup and integration",
        "Basic admin dashboard",
        "2 weeks of post-launch support",
        "Documentation and handoff"
      ]
    },
    {
      title: "Growth Scale",
      description: "For established products ready to scale",
      priceRange: "$15K - $30K",
      features: [
        "Advanced feature development",
        "Performance optimization",
        "Third-party integrations",
        "Custom API development",
        "Analytics implementation",
        "1 month of post-launch support",
        "Team training and documentation",
        "Scalability planning"
      ],
      isRecommended: true
    },
    {
      title: "Enterprise Build",
      description: "Comprehensive solutions for complex requirements",
      priceRange: "$30K+",
      features: [
        "Full-scale application development",
        "Microservices architecture",
        "Advanced security implementation",
        "Multi-platform deployment",
        "Custom integrations",
        "3 months of dedicated support",
        "Comprehensive documentation",
        "Team training and workshops",
        "Ongoing consultation"
      ]
    }
  ];

  const supportPackages = [
    {
      icon: "Shield",
      title: "Standard Support",
      responseTime: "24-48 hour",
      features: [
        "Bug fixes and patches",
        "Security updates",
        "Performance monitoring",
        "Email support",
        "Monthly maintenance reports"
      ]
    },
    {
      icon: "Zap",
      title: "Priority Support",
      responseTime: "4-8 hour",
      features: [
        "All Standard Support features",
        "Priority bug resolution",
        "Feature enhancement requests",
        "Video call support",
        "Bi-weekly check-ins",
        "Emergency hotline access"
      ]
    }
  ];

  const communicationProtocols = [
    {
      icon: "MessageCircle",
      title: "Daily Updates",
      description: "Quick status updates via Slack or email to keep you informed of progress, blockers, and next steps.",
      frequency: "Daily"
    },
    {
      icon: "Video",
      title: "Weekly Demos",
      description: "Live demonstrations of completed features with opportunity for feedback and direction adjustments.",
      frequency: "Weekly"
    },
    {
      icon: "FileText",
      title: "Sprint Reports",
      description: "Detailed progress reports with completed tasks, upcoming work, and timeline updates.",
      frequency: "Bi-weekly"
    },
    {
      icon: "Calendar",
      title: "Planning Sessions",
      description: "Collaborative planning meetings to prioritize features, discuss technical decisions, and align on goals.",
      frequency: "As needed"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Services - Mr Mike Technologies | Full-Stack Development & Technical Consulting</title>
        <meta name="description" content="Professional full-stack development services for startups and agencies. Scalable architecture, performance optimization, and reliable technical consulting by Mr Mike Technologies." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <Breadcrumbs />

            <section className="mb-16 md:mb-24">
              <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                  <Icon name="Sparkles" size={16} />
                  <span>Production-Ready Solutions</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-foreground mb-6">
                  Services That Scale
                  <br />
                  <span className="text-primary">Your Business</span>
                </h1>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  From MVP to enterprise-scale applications, I deliver reliable technical solutions that drive measurable business outcomes. Clear communication, predictable timelines, and production-grade quality.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {services?.map((service, index) => (
                  <ServiceCard key={index} {...service} />
                ))}
              </div>
            </section>
 <section className="mb-16 md:mb-24">
              <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4 md:mb-6">
                  Project Investment
                </h2>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  Transparent pricing based on project scope and complexity. Every engagement includes clear deliverables, timeline commitments, and post-launch support.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {pricingPackages?.map((pkg, index) => (
                  <PricingCard key={index} {...pkg} />
                ))}
              </div>

              <div className="mt-8 md:mt-12 p-6 md:p-8 bg-card border border-border rounded-2xl">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  <Icon name="Info" size={24} color="var(--color-primary)" className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg md:text-xl font-bold text-foreground mb-2">
                      Custom Project Estimation
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                      Every project is unique. These ranges provide general guidance, but I'll provide a detailed estimate after understanding your specific requirements during our discovery call.
                    </p>
                  </div>
                </div>
              </div>
            </section>
            <section className="mb-16 md:mb-24">
              <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4 md:mb-6">
                  Development Process
                </h2>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  A proven methodology that delivers results on time and within budget. Every project follows this structured approach to ensure quality and transparency.
                </p>
              </div>

              <div className="space-y-8 md:space-y-12">
                {processSteps?.map((step, index) => (
                  <React.Fragment key={index}>
                    <ProcessStep {...step} />
                    {index < processSteps?.length - 1 && (
                      <div className="flex justify-center">
                        <Icon name="ArrowDown" size={24} color="var(--color-primary)" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </section>

            <section className="mb-16 md:mb-24">
              <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4 md:mb-6">
                  Technology Stack
                </h2>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  I work with modern, battle-tested technologies that ensure your application is scalable, maintainable, and future-proof.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {technologyStacks?.map((stack, index) => (
                  <TechnologyStack key={index} {...stack} />
                ))}
              </div>
            </section>

           

            <section className="mb-16 md:mb-24">
              <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4 md:mb-6">
                  Communication Protocols
                </h2>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  Clear, consistent communication is the foundation of successful projects. Here's how we'll stay aligned throughout development.
                </p>
              </div>

              <CommunicationProtocol protocols={communicationProtocols} />
            </section>

            <section className="mb-16 md:mb-24">
              <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4 md:mb-6">
                  Post-Launch Support
                </h2>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  Your application's success doesn't end at launch. I provide ongoing support to ensure stability, security, and continuous improvement.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {supportPackages?.map((pkg, index) => (
                  <SupportPackage key={index} {...pkg} />
                ))}
              </div>
            </section>

            <section className="mb-16 md:mb-24">
              <div className="p-8 md:p-12 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-3xl text-center">
                <div className="max-w-3xl mx-auto">
                  <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-primary/20 mb-6 md:mb-8">
                    <Icon name="Rocket" size={32} color="var(--color-primary)" />
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4 md:mb-6">
                    Ready to Build Something Great?
                  </h2>
                  <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8 leading-relaxed">
                    Let's discuss your project requirements and create a tailored solution that drives real business results. I'm currently accepting new projects for Q1 2025.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Link to="/contact">
                      <Button 
                        variant="default" 
                        size="lg"
                        iconName="Calendar"
                        iconPosition="left"
                      >
                        Schedule Discovery Call
                      </Button>
                    </Link>
                    <Link to="/portfolio">
                      <Button 
                        variant="outline" 
                        size="lg"
                        iconName="Eye"
                        iconPosition="left"
                      >
                        View Past Projects
                      </Button>
                    </Link>
                  </div>
                  <div className="mt-6 md:mt-8 flex items-center justify-center gap-2 text-sm text-success">
                    <div className="w-2 h-2 rounded-full bg-success animate-pulse-glow" />
                    <span className="font-medium">Available for new projects</span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Services;